import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CloseIcon from "@mui/icons-material/Close";

import { styles } from "../../pages/Tracks/styles";
import {
  ArtWorkAndCollaboratorsContainer,
  AudioPlayerTime,
  CloseButton,
  OuterPlayerContainer,
  PlayerContainer,
  AudioPlayerMainContainer,
  audioPlayerStyles,
} from "./styles";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
} from "../../contexts";
import { AudioPlayerProps } from "./types";
import { useAuth0 } from "@auth0/auth0-react";
import { Collaborators } from "./Collaborators";
import AddCommentModal from "../Modal/AddCommentModal";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { handleGetTrackComments } from "../../services/commentsService";
import {
  AudioComment,
  GetTrackTimeStampCommentsResponse,
  Role,
} from "../../proto/api_pb";
import TrackComments from "../Modal/TrackComments";
import { formatTime } from "./helper";
import WaveSurfer from "wavesurfer.js";

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  trackToPlay,
  isPlayButtonClicked,
  versionToPlay,
  isTrackForPlay,
  audioDetails,
  setTrackToPlay,
  setIsPlayButtonClicked,
  setVersionToPlay,
}) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [allComment, setAllComments] = useState<AudioComment>();

  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const { isAuthenticated } = useAuth0();
  const { enterprise, isEnterpriseAccount } = useContext(UserDetailsContext);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { getValidAccessToken } = useGetValidAccessToken();

  const audioSrc = isTrackForPlay
    ? trackToPlay?.url
    : versionToPlay?.track?.getTrackUrl();

  const handleGetComments = useCallback(async () => {
    const token = await getValidAccessToken();
    const res = await handleGetTrackComments(
      token,
      isTrackForPlay
        ? (trackToPlay?.track?.getId() ?? "")
        : (versionToPlay?.track?.getTrackId() ?? ""),
      isTrackForPlay
        ? (trackToPlay?.track?.getTrackVersionId() ?? "")
        : (versionToPlay?.track?.getId() ?? "")
    );
    if (res instanceof GetTrackTimeStampCommentsResponse) {
      setAllComments(res.getAudioComment());
    }
  }, [
    getValidAccessToken,
    isTrackForPlay,
    trackToPlay?.track,
    versionToPlay?.track,
  ]);

  const refreshComments = async () => {
    await handleGetComments();
  };

  const handlePauseTrack = useCallback(() => {
    waveSurferRef.current?.pause();

    setIsPlaying(false);
    if (trackToPlay && setTrackToPlay) {
      setTrackToPlay({ ...trackToPlay, isPlaying: false });
    } else if (versionToPlay && setVersionToPlay) {
      setVersionToPlay({ ...versionToPlay, isPlaying: false });
    }
  }, [trackToPlay, setTrackToPlay, versionToPlay, setVersionToPlay]);

  const handlePlayTrack = useCallback(() => {
    waveSurferRef.current?.play();

    setIsPlaying(true);
    if (trackToPlay && setTrackToPlay) {
      setTrackToPlay({ ...trackToPlay, isPlaying: true });
    } else if (versionToPlay && setVersionToPlay) {
      setVersionToPlay({ ...versionToPlay, isPlaying: true });
    }
  }, [trackToPlay, setTrackToPlay, versionToPlay, setVersionToPlay]);

  const handlePlayPause = useCallback(() => {
    isPlaying ? handlePauseTrack() : handlePlayTrack();
  }, [isPlaying, handlePlayTrack, handlePauseTrack]);

  // Detect external pause/play
useEffect(() => {
  const shouldBePlaying = trackToPlay?.isPlaying || versionToPlay?.isPlaying;

  if (shouldBePlaying && !isPlaying) {
    // Start playing if not already playing
    handlePlayTrack();
  } else if (!shouldBePlaying && isPlaying) {
    // Pause if nothing should be playing
    handlePauseTrack();
  }
}, [
  isPlaying,
  trackToPlay?.isPlaying,
  versionToPlay?.isPlaying,
  handlePlayTrack,
  handlePauseTrack,
]);


  useEffect(() => {
    if (!waveformRef.current || !audioSrc) return;

    setIsAudioLoading(true);

    if (waveSurferRef.current) {
      waveSurferRef.current.destroy();
    }

    const waveSurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#a0a0a0",
      progressColor: "#1976d2",
      height: 60,
      barWidth: 2,
    });

    waveSurfer.load(audioSrc);

    waveSurfer.on("ready", () => {
      setDuration(waveSurfer.getDuration());
      setIsAudioLoading(false);

      const shouldAutoplay =
        (trackToPlay?.isPlaying || versionToPlay?.isPlaying) &&
        isPlayButtonClicked;

      if (shouldAutoplay) {
        waveSurfer.play();
        setIsPlaying(true);
      }
    });

    waveSurfer.on("audioprocess", () => {
      setCurrentTime(waveSurfer.getCurrentTime());
    });

    waveSurfer.on("interaction", () => {
      setCurrentTime(waveSurfer.getCurrentTime());
    });

    waveSurferRef.current = waveSurfer;

    return () => {
      waveSurfer.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    audioSrc,
    isPlayButtonClicked,
  ]);

  useEffect(() => {
    setAllComments(new AudioComment());
    if (trackToPlay || versionToPlay) handleGetComments();
  }, [handleGetComments, trackToPlay, versionToPlay]);

  const imageUrl = isTrackForPlay
    ? trackToPlay?.track?.getImageUrl()
    : audioDetails?.image;

  return (
    isPlayButtonClicked && (
      <AudioPlayerMainContainer
        style={!isAuthenticated ? { width: "100%" } : {}}
      >
        <Box sx={audioPlayerStyles.imageTitleContainer}>
          <ArtWorkAndCollaboratorsContainer>
            <img
              style={styles.image}
              src={
                imageUrl && imageUrl.includes("http")
                  ? imageUrl
                  : isEnterpriseAccount
                    ? enterprise?.image
                    : "/assets/images/logo512.png"
              }
              alt=""
            />
          </ArtWorkAndCollaboratorsContainer>
          <Typography data-testid="audio-title"  sx={{ minWidth: "100px" }}>
            {isTrackForPlay
              ? trackToPlay?.track?.getTitle()
              : audioDetails?.title}
          </Typography>
        </Box>

        <OuterPlayerContainer>
          <ArtWorkAndCollaboratorsContainer>
            {trackToPlay?.track && (
              <Collaborators
                collaborators={trackToPlay?.track.getCollaboratorsList()}
                isGridView={true}
              />
            )}
          </ArtWorkAndCollaboratorsContainer>

          <Box sx={{ display: "flex" }}>
            {isEnterpriseAccount &&
              (enterprise?.role !== Role.ROLE_ARTIST.toString() ||
                trackToPlay?.track
                  ?.getCollaboratorsList()
                  .some((c) => c.getUserId() === userId)) && (
                <AddCommentModal
                  trackId={
                    isTrackForPlay
                      ? (trackToPlay?.track?.getId() ?? "")
                      : (versionToPlay?.track?.getTrackId() ?? "")
                  }
                  versionId={
                    isTrackForPlay
                      ? (trackToPlay?.track?.getTrackVersionId() ?? "")
                      : (versionToPlay?.track?.getId() ?? "")
                  }
                  commentTime={currentTime}
                  refreshComments={refreshComments}
                  pauseTrack={handlePauseTrack}
                  playTrack={handlePlayTrack}
                />
              )}
            <TrackComments allComment={allComment} />
          </Box>

          <PlayerContainer>
            {isAudioLoading ? (
              <CircularProgress sx={{ color: "white" }} size={22} />
            ) : (
              <IconButton data-testid={isPlaying ?"pause-button":"play-button"} onClick={handlePlayPause}>
                {isPlaying ? (
                  <PauseCircleOutlineOutlinedIcon sx={{ fontSize: 30 }} />
                ) : (
                  <PlayCircleOutlineIcon sx={{ fontSize: 30 }} />
                )}
              </IconButton>
            )}
          </PlayerContainer>

          {!isSmallScreen && (
            <Box sx={{ width: "100%", marginLeft: "1rem" }}>
              <div ref={waveformRef} />
            </Box>
          )}

          <AudioPlayerTime>
            {formatTime(currentTime)} / {formatTime(duration)}
          </AudioPlayerTime>
        </OuterPlayerContainer>

        <Tooltip title="Close">
          <CloseButton
            data-testid="close-button"
            sx={isSmallScreen ? { marginRight: -2 } : {}}
            onClick={() => {
              waveSurferRef.current?.pause();
              if (isTrackForPlay) {
                setTrackToPlay((prev) => ({
                  ...prev!,
                  isPlaying: false,
                }));
              } else {
                if (setVersionToPlay) {
                  setVersionToPlay((prev) => ({
                    ...prev!,
                    isPlaying: false,
                    isSelected: true,
                  }));
                }
              }
              setIsPlayButtonClicked(false);
            }}
          >
            <CloseIcon />
          </CloseButton>
        </Tooltip>
      </AudioPlayerMainContainer>
    )
  );
};
